import { reaction } from 'mobx'
import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useRunState } from '../../provider/useRunState'
import type { TrackRegionState } from './trackController'

type TrackVisibilityControllerProps = {
    trackGeo: THREE.Group
}

export function TrackVisibilityController({
    trackGeo,
}: TrackVisibilityControllerProps) {
    const runState = useRunState()
    const unlockedRegionsRef = useRef<Set<1 | 2 | 3 | 4>>(new Set([1]))

    useEffect(() => {
        const syncVisibility = () => {
            if (runState.phase === 'idle') {
                unlockedRegionsRef.current = new Set([1])
            }

            const regionState = runState.trackRegionState
            unlockedRegionsRef.current = getUnlockedTrackRegionsForState(
                unlockedRegionsRef.current,
                regionState,
            )

            if (!regionState.activeTransition) {
                setTrackRegionVisibility(
                    trackGeo,
                    unlockedRegionsRef.current,
                    getHiddenTrackTransitions(),
                )
                return
            }

            const nextRegion = getNextTrackRegion(regionState.activeRegion)
            if (nextRegion && !unlockedRegionsRef.current.has(nextRegion)) {
                unlockedRegionsRef.current = new Set([
                    ...unlockedRegionsRef.current,
                    nextRegion,
                ])
            }

            setTrackRegionVisibility(
                trackGeo,
                getVisibleTrackRegionsForTransition(
                    unlockedRegionsRef.current,
                    regionState.activeRegion,
                ),
                getVisibleTrackTransitions(regionState.activeRegion),
            )
        }

        syncVisibility()

        return reaction(
            () => [
                runState.phase,
                runState.trackRegionState.activeRegion,
                runState.trackRegionState.activeTransition,
            ] as const,
            syncVisibility,
        )
    }, [runState, trackGeo])

    return null
}

function getUnlockedTrackRegionsForState(
    unlockedRegions: ReadonlySet<1 | 2 | 3 | 4>,
    regionState: TrackRegionState,
): Set<1 | 2 | 3 | 4> {
    const nextUnlocked = new Set(unlockedRegions)

    for (const region of [1, 2, 3, 4] as const) {
        if (region <= regionState.activeRegion) {
            nextUnlocked.add(region)
        }
    }

    if (regionState.activeTransition) {
        const nextRegion = getNextTrackRegion(regionState.activeRegion)
        if (nextRegion) {
            nextUnlocked.add(nextRegion)
        }
    }

    return nextUnlocked
}

function setTrackRegionVisibility(
    trackGeo: THREE.Group,
    visibleRegions: ReadonlySet<1 | 2 | 3 | 4>,
    visibleTransitions: ReadonlySet<1 | 2 | 3>,
) {
    for (const region of [1, 2, 3, 4] as const) {
        const mesh = trackGeo.getObjectByName(`TrackRegion${region}`)
        if (!mesh) {
            continue
        }
        mesh.visible = visibleRegions.has(region)
    }

    for (const transition of [1, 2, 3] as const) {
        const transitionMesh = trackGeo.getObjectByName(
            `TrackTransition${transition}`,
        )
        if (!transitionMesh) {
            continue
        }
        transitionMesh.visible = visibleTransitions.has(transition)
    }
}

function getVisibleTrackRegionsForTransition(
    unlockedRegions: ReadonlySet<1 | 2 | 3 | 4>,
    activeRegion: 1 | 2 | 3 | 4,
) {
    const visibleRegions = new Set<1 | 2 | 3 | 4>()

    for (const region of unlockedRegions) {
        if (region > activeRegion) {
            visibleRegions.add(region)
        }
    }

    return visibleRegions
}

function getNextTrackRegion(
    region: 1 | 2 | 3 | 4,
): 2 | 3 | 4 | null {
    switch (region) {
        case 1:
            return 2
        case 2:
            return 3
        case 3:
            return 4
        case 4:
            return null
    }
}

function getVisibleTrackTransitions(
    activeRegion: 1 | 2 | 3 | 4,
): ReadonlySet<1 | 2 | 3> {
    switch (activeRegion) {
        case 1:
            return new Set([1])
        case 2:
            return new Set([2])
        case 3:
            return new Set([3])
        case 4:
            return new Set()
    }
}

function getHiddenTrackTransitions(): ReadonlySet<1 | 2 | 3> {
    return new Set()
}
